import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  type CalendarEvent,
  type FlatTask,
  type TaskStatus,
  fetchTasksDashboard,
} from "./api";
import { CustomSelect } from "@/components/ui/CustomSelect";

type ViewMode = "calendar" | "list";

export function MyTasksPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const month = searchParams.get("month") || "";
  const status = searchParams.get("status") || "";
  const [view, setView] = useState<ViewMode>("calendar");
  const [search, setSearch] = useState("");
  const [modalDay, setModalDay] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["tasks-my", { month, status }],
    queryFn: () => fetchTasksDashboard({ month, status }),
  });

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next);
  };

  const goToday = () => {
    const today = new Date();
    const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    updateParams({ month: ym });
  };

  return (
    <div className="tasks-root">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t("tasks.title")}</h1>
          <p className="page-subtitle">{t("tasks.subtitle")}</p>
        </div>
      </div>

      {query.data && (
        <div className="kpi-grid" style={{ marginBottom: "1.25rem" }}>
          <div className="kpi-card">
            <div className="kpi-label">{t("tasks.kpi.label_total")}</div>
            <div className="kpi-value">{query.data.counts.total}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{t("tasks.kpi.label_pending")}</div>
            <div className={`kpi-value${query.data.counts.pending > 0 ? " kpi-value-accent" : ""}`}>
              {query.data.counts.pending}
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{t("tasks.kpi.label_done")}</div>
            <div className="kpi-value">{query.data.counts.done}</div>
          </div>
        </div>
      )}

      {query.isLoading && <p>{t("common.loading", { defaultValue: "Cargando…" })}</p>}
      {query.isError && (
        <p className="flash flash-error">
          {t("common.error", { defaultValue: "Ocurrió un error" })}
        </p>
      )}

      {query.data && (
        <div className="card calendar-card">
          <div className="calendar-toolbar filter-bar">
            <div className="calendar-month-nav">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => updateParams({ month: query.data.prev_month_value })}
              >
                ‹
              </button>
              <div className="calendar-month-meta">
                <div className="calendar-month-label">
                  {formatMonthLabel(query.data.month_value, locale)}
                </div>
                <div className="calendar-month-subtitle">
                  <span>{t("tasks.month_subtitle")}</span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => updateParams({ month: query.data.next_month_value })}
              >
                ›
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={goToday}
              >
                {t("tasks.today")}
              </button>
            </div>

            <div className="calendar-toolbar-actions">
              <div
                className="task-view-toggle"
                role="tablist"
                aria-label={t("tasks.view_mode")}
              >
                <button
                  type="button"
                  className={
                    "task-view-link" + (view === "calendar" ? " is-active" : "")
                  }
                  onClick={() => setView("calendar")}
                >
                  {t("tasks.view.calendar")}
                </button>
                <button
                  type="button"
                  className={
                    "task-view-link" + (view === "list" ? " is-active" : "")
                  }
                  onClick={() => setView("list")}
                >
                  {t("tasks.view.list")}
                </button>
              </div>
            </div>

            <form
              className="calendar-filters"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="form-field">
                <label htmlFor="month" className="form-label">
                  {t("tasks.month")}
                </label>
                <input
                  type="month"
                  id="month"
                  className="form-input"
                  value={query.data.month_value}
                  onChange={(e) => updateParams({ month: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label htmlFor="status" className="form-label">
                  {t("tasks.status")}
                </label>
                <CustomSelect
                  value={status}
                  options={query.data.all_statuses.map((s) => ({ value: s, label: t(`tasks.status_label.${s}`) }))}
                  onChange={(v) => updateParams({ status: v || null })}
                  emptyLabel={t("tasks.status_all")}
                />
              </div>
              <div className="calendar-filter-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    updateParams({ month: null, status: null })
                  }
                >
                  {t("tasks.reset")}
                </button>
              </div>
            </form>
          </div>

          {view === "calendar" ? (
            <CalendarView
              weeks={query.data.weeks}
              eventsByDay={query.data.events_by_day}
              onMore={(iso) => setModalDay(iso)}
              onEventClick={(taskId) => navigate(`/tasks/${taskId}`)}
              dayShort={
                t("tasks.day_short", { returnObjects: true }) as string[]
              }
            />
          ) : (
            <ListView
              tasks={query.data.tasks}
              search={search}
              onSearchChange={setSearch}
              locale={locale}
            />
          )}
        </div>
      )}

      {modalDay && query.data && (
        <DayModal
          day={modalDay}
          events={query.data.events_by_day[modalDay] || []}
          onClose={() => setModalDay(null)}
          locale={locale}
        />
      )}
    </div>
  );
}

function CalendarView(props: {
  weeks: { iso: string; day: number; in_month: boolean; is_today: boolean }[][];
  eventsByDay: Record<string, CalendarEvent[]>;
  onMore: (iso: string) => void;
  onEventClick: (taskId: string) => void;
  dayShort: string[];
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div
        className="calendar-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          marginBottom: 4,
        }}
      >
        {props.dayShort.map((d, i) => (
          <div
            key={i}
            className="summary-card-meta"
            style={{ textAlign: "center", padding: "4px 0" }}
          >
            {d}
          </div>
        ))}
      </div>
      <div className="calendar-grid">
        {props.weeks.flatMap((week) =>
          week.map((cell) => {
            const events = props.eventsByDay[cell.iso] || [];
            const visible = events.slice(0, 3);
            const more = events.length - visible.length;
            return (
              <div
                key={cell.iso}
                className={
                  "calendar-cell" +
                  (!cell.in_month ? " calendar-cell-muted" : "") +
                  (cell.is_today ? " calendar-cell-today" : "")
                }
              >
                <div className="calendar-cell-top">
                  <div className="calendar-day-number">{cell.day}</div>
                  {events.length > 0 && (
                    <span className="calendar-day-count">{events.length}</span>
                  )}
                  {cell.is_today && (
                    <span className="chip chip-today">{t("tasks.today")}</span>
                  )}
                </div>
                <div className="calendar-events">
                  {events.length === 0 ? (
                    <div className="calendar-empty">
                      {t("tasks.cell.no_tasks")}
                    </div>
                  ) : (
                    <>
                      {visible.map((ev) => (
                        <button
                          key={ev.task_id}
                          type="button"
                          className="calendar-event"
                          onClick={() => props.onEventClick(ev.task_id)}
                          style={{
                            background: "none",
                            border: 0,
                            padding: 0,
                            textAlign: "left",
                            cursor: "pointer",
                            width: "100%",
                          }}
                        >
                          <span
                            className={
                              "event-dot priority-" + ev.priority.toLowerCase()
                            }
                          />
                          <div className="calendar-event-body">
                            <div className="calendar-event-title">{ev.title}</div>
                            <div className="calendar-event-meta">
                              <span className="calendar-time">
                                {ev.time_label ||
                                  ev.start_time ||
                                  ev.end_time ||
                                  t("tasks.all_day")}
                              </span>
                              <span
                                className={"status-dot " + statusClass(ev.status)}
                                title={ev.status}
                              />
                            </div>
                          </div>
                        </button>
                      ))}
                      {more > 0 && (
                        <button
                          type="button"
                          className="link-button calendar-show-more"
                          onClick={() => props.onMore(cell.iso)}
                        >
                          {t("tasks.cell.more", { count: more })}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ListView(props: {
  tasks: FlatTask[];
  search: string;
  onSearchChange: (v: string) => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const filtered = useMemo(() => {
    const term = props.search.trim().toLowerCase();
    if (!term) return props.tasks;
    return props.tasks.filter(
      (x) =>
        x.title.toLowerCase().includes(term) ||
        x.description.toLowerCase().includes(term)
    );
  }, [props.tasks, props.search]);

  return (
    <div className="tasks-list-view">
      <div className="tasks-list-toolbar">
        <div className="tasks-list-search">
          <label className="form-label" htmlFor="tasksListSearch">
            {t("tasks.list.search")}
          </label>
          <input
            id="tasksListSearch"
            className="form-input"
            type="search"
            value={props.search}
            onChange={(e) => props.onSearchChange(e.target.value)}
            placeholder={t("tasks.list.search_placeholder")}
          />
        </div>
        <div className="summary-card-meta">
          {t("tasks.list.showing", {
            shown: filtered.length,
            total: props.tasks.length,
          })}
        </div>
      </div>
      <div>
        {filtered.length === 0 ? (
          <div className="empty-state">{t("tasks.list.empty")}</div>
        ) : (
          <table className="table table-sticky">
            <thead>
              <tr>
                <th>{t("tasks.detail.start")}</th>
                <th>{t("tasks.detail.priority")}</th>
                <th>{t("tasks.title")}</th>
                <th>{t("tasks.detail.my_status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((task) => (
                <tr key={task.task_id}>
                  <td>{formatDateTime(task.start_date, props.locale)}</td>
                  <td>
                    <span
                      className={"priority-chip priority-" + task.priority.toLowerCase()}
                    >
                      {t(`tasks.priority_label.${task.priority}`)}
                    </span>
                  </td>
                  <td>{task.title}</td>
                  <td>
                    <span className={"status-chip " + statusClass(task.status)}>
                      {t(`tasks.status_label.${task.status}`)}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`/tasks/${task.task_id}`}
                      className="btn btn-secondary btn-sm"
                    >
                      {t("docs.action.open", { defaultValue: "Abrir" })}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DayModal(props: {
  day: string;
  events: CalendarEvent[];
  onClose: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const dateLabel = formatDate(props.day, props.locale);
  return (
    <div
      className="task-modal"
      onClick={props.onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="task-modal-dialog card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: "92%", maxHeight: "85vh", overflow: "auto" }}
      >
        <div className="task-modal-header">
          <div>
            <div className="task-modal-label">{t("tasks.modal.title")}</div>
            <div className="task-modal-title">{dateLabel}</div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>
        <div className="task-modal-body" style={{ marginTop: 12 }}>
          {props.events.map((ev) => (
            <Link
              key={ev.task_id}
              to={`/tasks/${ev.task_id}`}
              className="calendar-event"
              onClick={props.onClose}
              style={{ display: "flex", marginBottom: 8 }}
            >
              <span className={"event-dot priority-" + ev.priority.toLowerCase()} />
              <div className="calendar-event-body">
                <div className="calendar-event-title">{ev.title}</div>
                <div className="calendar-event-meta">
                  <span className="calendar-time">
                    {ev.time_label || ev.start_time || ev.end_time || t("tasks.all_day")}
                  </span>
                  <span className={"status-dot " + statusClass(ev.status)} />
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="task-modal-actions" style={{ marginTop: 12, textAlign: "right" }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={props.onClose}
          >
            {t("tasks.modal.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function statusClass(s: TaskStatus): string {
  return "status-" + s.toLowerCase().replace("_", "-");
}

function formatMonthLabel(monthValue: string, locale: string): string {
  const [y, m] = monthValue.split("-").map(Number);
  if (!y || !m) return monthValue;
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    }).format(new Date(y, m - 1, 1));
  } catch {
    return monthValue;
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(
      new Date(iso + "T00:00:00")
    );
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
